import { useState } from "react";
import { useSetRecoilState } from "recoil";
import { userSimpleAtom } from "recoil/userSimpleAtom";
import { useNavigate } from "react-router-dom";
import {
  axiosInstancePromotion,
  CustomResponseFormat,
  setBearerTokenPromotion,
} from "api/instancePromotion";
import { syncFbcToAccount, trackCapiStandardEvent } from "analytics/metaCapi";
import { trackXAdsEvent } from "analytics/xAdsPixel";
import mixpanel from "mixpanel-browser";
import useMixpanelProfileMkt from "./useMixpanelProfileMkt";
import useMixpanelMKT from "./useMixpanelMKT";
import useGtagConversion from "./useGtagConversion";
import { useMutation } from "@tanstack/react-query";
import { webFunnelApi } from "../api/webFunnelApi";
import {
  affiliateApi,
  AFFILIATE_REFERRAL_CODE_STORAGE_KEY,
} from "../api/affiliateApi";
import { captureException } from "../utils/sentry";
import { PATH } from "../routes/paths";

export default function useLoginPromotion({
  url,
  idToken,
  code,
  type,
  email,
  redirectUri,
}: {
  url: string;
  idToken?: string;
  code?: string;
  type: "google" | "apple" | "kakao";
  email?: string;
  redirectUri?: string;
}) {
  let navigate = useNavigate();
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string>();
  const setUser = useSetRecoilState(userSimpleAtom);
  const setPromotionPeople = useMixpanelProfileMkt("v4");

  const trackGtagEvent = useGtagConversion();

  const trackMixpanelRegistration = useMixpanelMKT({
    params: {
      method: type,
    },
    eventName: "가입",
    version: "v4",
  });

  const trackMixpanelLogin = useMixpanelMKT({
    params: {
      method: type,
    },
    eventName: "로그인",
    version: "v4",
  });

  const { mutate: identify } = useMutation(
    ({ distinctId }: { distinctId: string }) =>
      webFunnelApi.identify(distinctId),
    {
      onError: (error, variables) => {
        captureException(error, {
          tags: { action: "web_funnel_identify" },
          extra: { distinctId: variables.distinctId },
        });
      },
    }
  );

  const login = async () => {
    if (isLoading) return;

    setIsLoading(true);
    setError(undefined);
    try {
      const response = await axiosInstancePromotion.post<CustomResponseFormat>(
        url,
        {
          type: type,
          ...(idToken && { idToken: idToken }),
          ...(code && { code: code }),
          ...(email && { email: email }),
          ...(redirectUri && { redirectUri: redirectUri }),
          // 제휴 진입(레퍼럴 코드 보유) 시 신규 가입을 앱 온보딩 유저로 등록(isWebFunnelRegister=false)
          ...(localStorage.getItem(AFFILIATE_REFERRAL_CODE_STORAGE_KEY) && {
            isAffiliate: true,
          }),
        }
      );

      if (response.code === 0) {
        const result = response.result;
        setBearerTokenPromotion(result.accessToken);
        setUser(result);

        // 제휴(affiliate) 진입 코드가 있으면 로그인된 유저에 영속화 (last-touch).
        // 일반 프로모션 로그인은 키가 없으므로 no-op. 실패해도 로그인 흐름은 막지 않음.
        const referralCode = localStorage.getItem(
          AFFILIATE_REFERRAL_CODE_STORAGE_KEY
        );
        if (referralCode) {
          try {
            await affiliateApi.saveReferralCode(referralCode);
          } catch (e) {
            captureException(e, {
              tags: { action: "affiliate_referral_save" },
            });
          } finally {
            localStorage.removeItem(AFFILIATE_REFERRAL_CODE_STORAGE_KEY);
          }
        }

        const distinctId =
          localStorage.getItem("WEB_FUNNEL_DISTINCT_ID") ||
          (mixpanel as any).promotion?.get_distinct_id();

        if (distinctId) {
          identify({ distinctId: distinctId });
        }

        if (result.isFirstLogin) {
          // Track CompleteRegistration event with userId and email
          trackCapiStandardEvent("CompleteRegistration", {
            value: 0,
            currency: "KRW",
            externalId: result.userId,
            email: result.email,
            // 제휴 코드는 userData(externalId/email)가 아니므로 customData 로 서버 CAPI 전송됨
            ...(referralCode && { affiliate_code: referralCode }),
          });

          trackGtagEvent("CompleteRegistration", "v4", {
            email: result.email,
          });

          trackXAdsEvent("Lead", {
            value: 0,
            currency: "KRW",
            email: result.email,
            status: "completed",
          });

          trackMixpanelRegistration();
        } else {
          trackMixpanelLogin();

          // 재로그인은 CAPI 이벤트가 없어 서버 저장 fbc가 갱신되지 않는다 — 최신 클릭(fbc)을 직접 동기화
          syncFbcToAccount(result.userId);
        }

        const promotionInstance = (mixpanel as any).promotion;
        if (promotionInstance && result.trackId) {
          setPromotionPeople({
            $email: result.email,
            user_id: result.userId,
            "테스트 유형": "C",
            "로그인 페이지 유형": "E",
            ...(referralCode && { "제휴 코드": referralCode }),
            ...(result.nickname && { $name: result.nickname }),
            ...(result.paymentPageType && {
              "결제 페이지 유형": result.paymentPageType.toUpperCase(),
            }),
            ...(result.pricingModel && {
              "결제 모델 유형": result.pricingModel.toUpperCase(),
            }),
            ...(result.onboardingType && {
              "온보딩 유형": result.onboardingType.toUpperCase(),
            }),
          });
        }

        if (referralCode) {
          // 제휴 진입 → eligibility 확인 + 앱 딥링킹/안내는 결과 화면이 담당
          navigate(PATH.affiliateResult, { replace: true });
        } else if (result.isFirstLogin) {
          navigate("/promotion-onboarding/nickname/1", { replace: true });
        } else {
          if (result?.hasAlreadyUsed) {
            navigate(PATH.promotionAlreadyUsed, { replace: true });
          } else {
            navigate("/promotion-onboarding/auth-loading/1", { replace: true });
          }
        }
      } else {
        setError(`로그인에 실패했습니다. 다시 시도해주세요.: ${response.code}`);
      }
    } catch (error) {
      console.error("Login failed:", error);
      if (!error) {
        setError(`로그인에 실패했습니다. 다시 시도해주세요. ${error}`);
      }
      throw error;
    } finally {
      setIsLoading(false);
    }
  };

  return { login, isLoading, error };
}
